import React, { ReactNode, useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import IconArrowDown from 'components/icons/arrow-down';
import PaymentMethodOption, { IPaymentMethodOptionProps } from 'components/payment-method-option';
import { IPaymentMethod } from 'state/payment/types';
import { routes } from 'utils/routing';

import { AddNewCardLink } from './add-new-card-link';
import { PAYMENT_METHOD_OPTION_HEIGHT } from './constants';
import { HoverTheme, IHover } from './types';

export { HoverTheme } from './types';

interface IStyledPaymentMethodOption extends IPaymentMethodOptionProps {
  hoverTheme: HoverTheme;
}
interface IDisplayProps {
  open: boolean;
  children?: ReactNode[];
  hasAddPaymentMethod?: boolean;
}

interface IPaymentMethods {
  selectedMethod: IPaymentMethod | undefined;
  nonSelectedMethods: IPaymentMethod[];
  onOptionClick: (arg0: string) => void;
  onNavigateClick?: (arg0: MouseEvent, arg1: string) => void;
  hoverTheme?: HoverTheme;
}

interface IPaymentMethodsContainer {
  hoverTheme: HoverTheme;
}

const DisplayToggle = styled.div<IHover>`
  position: absolute;
  right: 0.875rem;
  top: 1.15rem;
  pointer-events: none;
  z-index: ${Styles.zIndex.normal};

  & svg {
    display: block;
    fill: ${p => (p.hoverTheme === HoverTheme.DARK ? Styles.color.white : Styles.color.grey.two)};
    height: 0.75rem;
    width: 0.75rem;
  }

  & a {
    display: none;
  }
`;

const StyledPaymentMethodOption = styled(PaymentMethodOption)<IStyledPaymentMethodOption>`
  height: ${PAYMENT_METHOD_OPTION_HEIGHT}rem;
  z-index: ${Styles.zIndex.below};

  & span {
    font-size: 12px;
    margin-top: 2px; // to make font appear vertically centred
    color: ${p => (p.hoverTheme === HoverTheme.DARK ? Styles.color.white : Styles.color.grey.two)};
  }

  &:hover {
    background-color: ${p => (p.hoverTheme === HoverTheme.DARK ? '#A5031D' : Styles.color.primary)};
    color: ${p => (p.hoverTheme === HoverTheme.DARK ? 'inherit' : Styles.color.white)};

    & span {
      color: ${Styles.color.white};
    }
  }
`;

export const PaymentMethodsContainer = styled.div<IPaymentMethodsContainer>`
  position: relative;
  width: 100%;
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 4px;
  margin-bottom: 1.5rem;
  overflow: hidden;

  & ${StyledPaymentMethodOption} {
    border-bottom: 1px solid
      ${p =>
        p.hoverTheme === HoverTheme.DARK ? 'rgba(255, 255, 255, 0.5)' : Styles.color.grey.four};
  }
`;

const PaymentMethods = styled.div`
  &:hover svg {
    fill: ${Styles.color.white};
  }
`;

const HiddenPaymentMethods = styled.div<IDisplayProps>`
  height: ${p =>
    p.open
      ? (p.hasAddPaymentMethod
          ? React.Children.count(p.children)
          : React.Children.count(p.children) - 1) * PAYMENT_METHOD_OPTION_HEIGHT
      : 0}rem;
  overflow: hidden;
  transition: height 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
`;

const AnimatedArrow = styled(IconArrowDown)<IDisplayProps>`
  transform: rotate(${p => (p.open ? '-180deg' : '0')});
  transition: transform 0.3s ease;
`;

const PaymentMethodSelect: React.FC<IPaymentMethods> = ({
  selectedMethod,
  nonSelectedMethods,
  onOptionClick,
  onNavigateClick,
  hoverTheme = HoverTheme.DARK
}) => {
  const { formatMessage } = useIntl();
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);

  const handleOptionClick = useCallback(
    (fdAccountId: string) => {
      onOptionClick(fdAccountId);
      // Delay closing payment methods UI
      setTimeout(() => setShowPaymentMethods(false), 200);
    },
    [onOptionClick]
  );

  const handleTogglePaymentMethods = useCallback(() => {
    if (nonSelectedMethods.length > 0 || onNavigateClick) {
      setShowPaymentMethods(currentShowPaymentMethods => !currentShowPaymentMethods);
    }
  }, [setShowPaymentMethods, nonSelectedMethods.length, onNavigateClick]);

  const handleNavigateClick = useCallback(
    e => {
      if (onNavigateClick) {
        onNavigateClick(e, routes.payment);
      }
    },
    [onNavigateClick]
  );

  useEffect(() => {
    setShowPaymentMethods(!selectedMethod);
  }, [selectedMethod]);

  // Show the arrow if the user has more that one payment method
  // or if the user has a selected payment method and the dropdown has a link to add a new CC
  const showArrow = !!nonSelectedMethods.length || (selectedMethod && onNavigateClick);

  return (
    <PaymentMethodsContainer hoverTheme={hoverTheme}>
      {selectedMethod && (
        <PaymentMethods>
          {showArrow && (
            <DisplayToggle hoverTheme={hoverTheme} onClick={handleTogglePaymentMethods}>
              <AnimatedArrow open={showPaymentMethods} />
            </DisplayToggle>
          )}
          <StyledPaymentMethodOption
            key={selectedMethod.fdAccountId}
            method={selectedMethod}
            selected
            onClick={handleTogglePaymentMethods}
            showDefault={false}
            hoverTheme={hoverTheme}
            disableHover
            hideBorder={!showPaymentMethods}
          />
        </PaymentMethods>
      )}
      <HiddenPaymentMethods open={showPaymentMethods} hasAddPaymentMethod={!!onNavigateClick}>
        {nonSelectedMethods.map((method: IPaymentMethod) => {
          const accountIdentifier = method.fdAccountId ?? method.accountIdentifier ?? '';
          return (
            <StyledPaymentMethodOption
              key={accountIdentifier}
              method={method}
              onClick={() => handleOptionClick(accountIdentifier)}
              showDefault={false}
              hoverTheme={hoverTheme}
              disableHover
            />
          );
        })}
        {onNavigateClick && (
          <AddNewCardLink hoverTheme={hoverTheme} onClick={handleNavigateClick}>
            {formatMessage({ id: 'addNewPaymentMethod' })}
          </AddNewCardLink>
        )}
      </HiddenPaymentMethods>
    </PaymentMethodsContainer>
  );
};

export default PaymentMethodSelect;
